import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table'
import { useMemo } from 'react'
import { TriangleAlert } from 'lucide-react'

import {
  ActionsDropdown,
  createDeleteAction,
  createDownloadAction,
  createEditAction,
  createExternalAction,
  createManageFilesAction,
  createSyncAction,
} from 'components/Buttons/ActionsDropdown'
import { OverflownTextTooltip } from 'components/OverflownTextTooltip'
import { useGetSyndigoSyncStatus, VariantCatalog } from 'services/products_api'
import { featureFlagService } from 'utils/featureFlagService'
import { formatDateWithTimeAndMinutes2 } from 'utils/formatting'
import { createTableColumnHelper } from 'components/DataExpansionComponents/Common/commonColumnHelpers'
import { VARIANT_COLUMNS } from './VariantColumnsInfo'
import { usePreferences } from 'context/preferences/PreferencesContext'

const columnHelper = createColumnHelper<VariantCatalog>()

type VariantTableColumnsProps = {
  handleVariantEdit: (variant: VariantCatalog) => void
  handleVariantDelete: (variant: VariantCatalog) => void
  handleVariantSync: (variant: VariantCatalog) => void
  handleOpenSyndigo: (variant: VariantCatalog) => void
  handleOpenSpecsheet: (variant: VariantCatalog) => void
  handleManageFiles: (variant: VariantCatalog) => void
  handleDownloadSpecsheet: (variant: VariantCatalog) => void
}

export function useVariantTableColumns({
  handleOpenSyndigo,
  handleVariantDelete,
  handleVariantEdit,
  handleVariantSync,
  handleOpenSpecsheet,
  handleManageFiles,
  handleDownloadSpecsheet,
}: VariantTableColumnsProps) {
  const {
    enableSyndigoIntegration,
    enableDocumentLibrary,
    enableBrandManagement,
  } = featureFlagService()

  const { data: syndigoSyncStatus } = useGetSyndigoSyncStatus()

  const { preferences } = usePreferences()
  const tooltips: Record<string, unknown> = preferences?.tooltips || {}
  const variantColumnHelper = createTableColumnHelper({
    columns: VARIANT_COLUMNS,
    tooltips,
  })

  const columns = useMemo<ColumnDef<VariantCatalog, any>[]>(() => {
    // Shared brand column definition used in both Syndigo and non-Syndigo modes
    const brandNameColumn = columnHelper.accessor('brand_name', {
      ...variantColumnHelper.retrieveColumn('brand_name'),
      size: 150,
      cell: (info) => {
        const value = info.getValue()
        if (!value) return '—'

        return (
          <OverflownTextTooltip
            tooltipText={value}
            style={{ maxWidth: '150px' }}
          >
            {value}
          </OverflownTextTooltip>
        )
      },
      enableSorting: true,
    })

    if (enableSyndigoIntegration) {
      return [
        columnHelper.accessor('name', {
          header: () => '',
          enableHiding: false,
          meta: {
            headerTitle: 'Variant Name',
            isFirstColumn: true,
            isEditable: false,
          },
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <OverflownTextTooltip
                tooltipText={value}
                style={{ maxWidth: '250px' }}
              >
                {value}
              </OverflownTextTooltip>
            )
          },
          size: 200,
          enableSorting: true,
        }),
        columnHelper.accessor('product_name', {
          header: 'Product',
          cell: (info) => {
            const productName = info.getValue()
            const productId = info.row.original.product_id
            const isUnmatched = !productName || !productId

            if (isUnmatched) {
              return (
                <div className="flex items-center gap-2 text-yellow-600">
                  <TriangleAlert size={16} className="flex-shrink-0" />
                  <span className="text-gray-400">No Product</span>
                </div>
              )
            }

            return productName
          },
          size: 150,
          enableSorting: true,
        }),
        columnHelper.accessor('sku', {
          header: 'SKU',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <OverflownTextTooltip
                tooltipText={value}
                style={{ maxWidth: '150px' }}
              >
                {value}
              </OverflownTextTooltip>
            )
          },
          size: 100,
          enableSorting: true,
        }),
        columnHelper.accessor('format_pack_size', {
          header: 'Format / pack size',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <OverflownTextTooltip
                tooltipText={value}
                style={{ maxWidth: '150px' }}
              >
                {value}
              </OverflownTextTooltip>
            )
          },
          size: 130,
          enableSorting: true,
        }),
        columnHelper.accessor('gtin', {
          header: 'GTIN / UPC',
          cell: (info) => {
            const value = info.getValue() || '—'
            return (
              <OverflownTextTooltip
                tooltipText={value}
                style={{ maxWidth: '250px' }}
              >
                {value}
              </OverflownTextTooltip>
            )
          },
          size: 130,
          enableSorting: true,
        }),
        columnHelper.accessor('avg_price', {
          header: 'Avg Price per Unit',
          cell: (info) => {
            const value = info.getValue()
            if (!value) return '—'
            // Format as currency
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(value)
          },
          size: 110,
          enableSorting: true,
        }),
        ...(enableBrandManagement ? [brandNameColumn] : []),
        columnHelper.display({
          ...variantColumnHelper.retrieveColumn('category'),
          size: 150,
          cell: ({ row }) => {
            const value = row.original.syndigo_product?.category
            if (!value) return '-'

            return <span>{value}</span>
          },
          enableSorting: true,
        }),
        columnHelper.display({
          ...variantColumnHelper.retrieveColumn('source'),
          size: 100,
          cell: ({ row }) => {
            const value = row.original.syndigo_product
            if (!value)
              return (
                <span className="bg-green-100 text-green-700 flex items-center justify-center w-[73px] rounded-full text-sm font-semibold py-1">
                  Firstbite
                </span>
              )

            return (
              <span className="bg-sky-100 text-sky-700 flex items-center justify-center w-[73px] rounded-full text-sm font-semibold py-1">
                Syndigo
              </span>
            )
          },
          enableSorting: true,
        }),
        columnHelper.display({
          ...variantColumnHelper.retrieveColumn('last_synced_at'),
          size: 120,
          cell: ({ row }) => {
            const value = row.original.syndigo_product?.last_synced_at
            if (!value) return '—'

            const { date, time } = formatDateWithTimeAndMinutes2(value)
            return (
              <div className="flex flex-col">
                <span>{date}</span>
                <span className="text-gray-500 text-sm">{time}</span>
              </div>
            )
          },
          enableSorting: true,
        }),
        {
          id: 'actions',
          header: 'Actions',
          size: 100,
          enableHiding: false,

          meta: {
            isEditable: false,
            headerDisableOrdering: true,
            isLastColumn: true,
            rightAlign: true,
            headerGroup: 'Actions',
          },
          cell: ({ row }: CellContext<VariantCatalog, unknown>) => {
            const variant = row.original
            const actions = [createEditAction(() => handleVariantEdit(variant))]

            // Add Manage Files action if document library is enabled
            if (enableDocumentLibrary) {
              actions.push(
                createManageFilesAction(() => handleManageFiles(variant))
              )
            }

            if (variant.syndigo_product) {
              actions.push(createSyncAction(() => handleVariantSync(variant)))
              actions.push(
                createExternalAction('Open on Syndigo', () =>
                  handleOpenSyndigo(variant)
                )
              )
              actions.push(
                createExternalAction('View Spec Sheet', () =>
                  handleOpenSpecsheet(variant)
                )
              )
              actions.push(
                createDownloadAction('Download Spec Sheet', () =>
                  handleDownloadSpecsheet(variant)
                )
              )
            } else {
              actions.push(
                createDeleteAction(() => handleVariantDelete(variant))
              )
            }

            return <ActionsDropdown actions={actions} containerWidth={180} />
          },
        },
      ]
    }

    return [
      columnHelper.accessor('name', {
        header: () => '',
        enableHiding: false,
        meta: {
          headerTitle: 'Variant Name',
          isFirstColumn: true,
          isEditable: false,
        },
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip
              tooltipText={value}
              style={{ maxWidth: '250px' }}
            >
              {value}
            </OverflownTextTooltip>
          )
        },
        size: 200,
        enableSorting: true,
      }),
      columnHelper.accessor('product_name', {
        header: 'Product',
        cell: (info) => {
          const productName = info.getValue()
          const productId = info.row.original.product_id
          const isUnmatched = !productName || !productId

          if (isUnmatched) {
            return (
              <div className="flex items-center gap-2 text-yellow-600">
                <TriangleAlert size={16} className="flex-shrink-0" />
                <span className="text-gray-400">No Product</span>
              </div>
            )
          }

          return productName
        },
        size: 180,
        enableSorting: true,
      }),
      columnHelper.accessor('sku', {
        header: 'SKU',
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip
              tooltipText={value}
              style={{ maxWidth: '150px' }}
            >
              {value}
            </OverflownTextTooltip>
          )
        },
        size: 130,
        enableSorting: true,
      }),
      columnHelper.accessor('format_pack_size', {
        header: 'Format / pack size',
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip
              tooltipText={value}
              style={{ maxWidth: '150px' }}
            >
              {value}
            </OverflownTextTooltip>
          )
        },
        size: 150,
        enableSorting: true,
      }),
      columnHelper.accessor('gtin', {
        header: 'GTIN / UPC',
        cell: (info) => {
          const value = info.getValue() || '—'
          return (
            <OverflownTextTooltip
              tooltipText={value}
              style={{ maxWidth: '130px' }}
            >
              {value}
            </OverflownTextTooltip>
          )
        },
        size: 120,
        enableSorting: true,
      }),
      columnHelper.accessor('avg_price', {
        header: 'Avg Price per Unit',
        cell: (info) => {
          const value = info.getValue()
          if (!value) return '—'
          // Format as currency
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value)
        },
        size: 110,
        enableSorting: true,
      }),
      ...(enableBrandManagement ? [brandNameColumn] : []),
      {
        id: 'actions',
        header: 'Actions',
        size: 100,
        enableHiding: false,

        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: true,
          headerGroup: 'Actions',
        },
        cell: ({ row }: CellContext<VariantCatalog, unknown>) => {
          const variant = row.original
          const actions = [createEditAction(() => handleVariantEdit(variant))]

          // Add Manage Files action if document library is enabled
          if (enableDocumentLibrary) {
            actions.push(
              createManageFilesAction(() => handleManageFiles(variant))
            )
          }

          if (variant.syndigo_product) {
            actions.push(
              createSyncAction(
                () => handleVariantSync(variant),
                syndigoSyncStatus?.status === 'in_progress' // Disable if any sync in progress
              )
            )
            actions.push(
              createExternalAction('Open on Syndigo', () =>
                handleOpenSyndigo(variant)
              )
            )
            actions.push(
              createExternalAction('View Spec Sheet', () =>
                handleOpenSpecsheet(variant)
              )
            )
            actions.push(
              createDownloadAction('Download Spec Sheet', () =>
                handleDownloadSpecsheet(variant)
              )
            )
          } else {
            actions.push(createDeleteAction(() => handleVariantDelete(variant)))
          }

          return <ActionsDropdown actions={actions} containerWidth={160} />
        },
      },
    ]
  }, [
    enableSyndigoIntegration,
    enableDocumentLibrary,
    syndigoSyncStatus,
    handleOpenSyndigo,
    handleVariantDelete,
    handleVariantEdit,
    handleVariantSync,
    handleOpenSpecsheet,
    handleManageFiles,
    handleDownloadSpecsheet,
    variantColumnHelper,
  ])

  return columns
}
